import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, ButtonBase, Stack, Typography} from 'views/components';
import {RefreshOutlined} from 'views/icons';
import {Translate} from 'views/module';

const RoutinesList = (props) => {
  const {routines, onSelectRoutine, onRefresh, selectedRoutineRn} = props;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '280px',
        border: '1px solid',
        borderColor: '#E8E4DE',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction='row'
        alignItems='flex-start'
        justifyContent='space-between'
        sx={{px: '32px', paddingTop: '24px', paddingBottom: '12px', gap: '8px'}}
      >
        <Stack sx={{gap: '8px'}}>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: '17px',
              color: '#1A1916',
              lineHeight: '100%',
              textDecoration: 'underline',
              textDecorationColor: '#DA7955',
              textUnderlineOffset: '5px',
            }}
          >
            <Translate expression='routines.List.Title' />
          </Typography>
          <Typography sx={{fontWeight: 400, fontSize: '12px', color: '#A8A49E', lineHeight: '100%', whiteSpace: 'nowrap'}}>
            <Translate expression='routines.List.Subtitle' />
          </Typography>
        </Stack>
        <Button
          onClick={onRefresh}
          disableElevation
          sx={{
            'minWidth': '0',
            'borderRadius': '7px',
            'p': '4px',
            'color': '#A8A49E',
            'backgroundColor': 'transparent',
            '&:hover': {backgroundColor: '#F3F1EE', boxShadow: 'none'},
          }}
        >
          <RefreshOutlined sx={{fontSize: 16}} />
        </Button>
      </Stack>

      <Box sx={{borderBottom: '1px solid #E8E4DE'}} />

      <Stack sx={{flex: 1, px: '8px', py: '8px', gap: '2px', overflowY: 'auto'}}>
        {routines.length === 0 && (
          <Typography sx={{px: '8px', py: '8px', fontSize: '12px', color: '#A8A49E', lineHeight: '150%'}}>
            <Translate expression='routines.List.Empty' />
          </Typography>
        )}
        {routines.map((routine) => {
          const isSelected = routine.routineRn === selectedRoutineRn;
          const isActive = routine.status === 'ACTIVE';

          return (
            <ButtonBase
              key={routine.routineRn}
              onClick={() => onSelectRoutine(routine)}
              sx={{
                'position': 'relative',
                'display': 'flex',
                'alignItems': 'center',
                'justifyContent': 'space-between',
                'width': '100%',
                'px': '12px',
                'py': '10px',
                'borderRadius': '8px',
                'textAlign': 'left',
                'backgroundColor': isSelected ? '#F3F1EE' : 'transparent',
                '&:hover': {backgroundColor: isSelected ? '#F3F1EE' : '#F8F6F3'},
                '&::before': isSelected ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: '20%',
                  bottom: '20%',
                  width: '4px',
                  borderRadius: '0 2px 2px 0',
                  backgroundColor: '#DA7955',
                } : {},
              }}
            >
              <Stack sx={{gap: '3px', minWidth: 0, flex: 1}}>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: isSelected ? 500 : 400,
                    color: '#1A1916',
                    lineHeight: '130%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {routine.title || routine.routineRn}
                </Typography>
                {routine.description && (
                  <Typography
                    sx={{
                      fontSize: '11px',
                      color: '#A8A49E',
                      lineHeight: '130%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {routine.description}
                  </Typography>
                )}
              </Stack>
              <Box
                sx={{
                  flexShrink: 0,
                  ml: '8px',
                  width: '7px',
                  height: '7px',
                  borderRadius: '50%',
                  backgroundColor: isActive ? '#10B981' : '#D1D5DB',
                }}
              />
            </ButtonBase>
          );
        })}
      </Stack>
    </Box>
  );
};

RoutinesList.propTypes = {
  routines: PropTypes.array.isRequired,
  onSelectRoutine: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  selectedRoutineRn: PropTypes.string,
};

export default React.memo(RoutinesList);
